import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Brain, TrendingUp, TrendingDown, Sparkles, Lightbulb } from "lucide-react";
import api from "../lib/api";

function StatCard({ label, value, tone }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={`learning-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold tabular-nums mt-1 ${tone || ""}`}>{value ?? "—"}</div>
    </div>
  );
}

export default function AcqLearning() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "ARQITEK — Learning Loop";
    api.get("/acq/learning").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const s = data?.stats || {};
  const ins = data?.insights;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="acq-learning-page">
      <Link to="/ai-office" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="learning-back">
        <ArrowLeft className="h-4 w-4" /> AI Office
      </Link>
      <div>
        <span className="block text-xs uppercase tracking-[0.15em] text-primary mb-2">Acquisition Intelligence · §19</span>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><Brain className="h-6 w-6 text-primary" /> Angela's Learning Loop</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Every lead you accept (Under Contract) or reject teaches Angela which signals actually convert — so her deal scores get sharper over time. Based only on your real outcomes.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm py-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <StatCard label="Outcomes" value={s.total} />
            <StatCard label="Accepted" value={s.accepted} tone="text-emerald-600" />
            <StatCard label="Rejected" value={s.rejected} tone="text-muted-foreground" />
            <StatCard label="Avg Score · Accepted" value={s.avg_score_accepted} tone="text-emerald-600" />
            <StatCard label="Avg Score · Rejected" value={s.avg_score_rejected} tone="text-destructive" />
            <StatCard label="Avg Motivation · Accepted" value={s.avg_motivation_accepted} />
          </div>

          {data?.note ? (
            <div className="rounded-xl border border-dashed border-border bg-card/40 p-6 flex items-start gap-3" data-testid="learning-note">
              <Lightbulb className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <p className="text-sm text-muted-foreground">{data.note}</p>
            </div>
          ) : ins ? (
            <div className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid="learning-insights">
              <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /><span className="font-semibold text-sm">Angela's Read</span>
                {ins.confidence && <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full border border-border text-muted-foreground">{ins.confidence} confidence</span>}
              </div>
              {ins.summary && <p className="text-sm">{ins.summary}</p>}
              {Array.isArray(ins.patterns) && ins.patterns.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Patterns</div>
                  <ul className="space-y-1.5">
                    {ins.patterns.map((p, i) => <li key={i} className="text-sm flex gap-2"><span className="text-primary">•</span>{p}</li>)}
                  </ul>
                </div>
              )}
              {Array.isArray(ins.score_adjustments) && ins.score_adjustments.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1"><TrendingDown className="h-3 w-3" /> Suggested Score Tuning</div>
                  <div className="space-y-2">
                    {ins.score_adjustments.map((a, i) => (
                      <div key={i} className="rounded-lg border border-border p-3 text-sm">
                        <span className="font-medium">{a.factor}</span> — <span className="text-muted-foreground">{a.suggestion}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
